import { useContext, useMemo } from 'react';
import { useFormikContext, Field } from 'formik';
import { FaSpinner } from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import {
  Form,
  FormGroup,
  Input,
  Label,
  FormFeedback,
  CustomInput,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import propTypes from 'prop-types';
import { ErrorMessage } from '@kiper/ui';
import {
  ButtonSubmit,
  ButtonLink,
  InputWrapper,
  ToggleVisibilityButton,
} from '../Login/styles';
import context from '../../../services/auth/context';
import { usePasswordValidation, usePasswordVisibility } from '../hooks';

const ResetPasswordForm = ({ hideLink, submitText }) => {
  const formikBag = useFormikContext();

  const auth = useContext(context);

  const [t] = useTranslation('reset-password');
  const history = useHistory();
  const { customErrors, validatePassword } = usePasswordValidation();
  const { handleSubmit, handleChange, values, errors, touched } = formikBag;

  const { visibility, toggleVisibility } = usePasswordVisibility([
    'password',
    'passwordConfirmation',
  ]);

  const renderVisibilityToggle = field => (
    <ToggleVisibilityButton onClick={() => toggleVisibility(field)}>
      {visibility[field] ? <FiEyeOff size={18} /> : <FiEye size={18} />}
    </ToggleVisibilityButton>
  );

  const goToLogin = () =>
    history.push({
      pathname: '/',
      search: `?email=${encodeURIComponent(values.username)}`,
    });

  const submitting = useMemo(() => auth.submitting, [auth]);

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <FormGroup>
        <CustomInput
          type="checkbox"
          id="check-lowercase"
          label={t('check.lowercase')}
          disabled
          checked={!customErrors.lowercase}
        />
        <CustomInput
          type="checkbox"
          id="check-uppercase"
          label={t('check.uppercase')}
          disabled
          checked={!customErrors.uppercase}
        />
        <CustomInput
          type="checkbox"
          id="check-number"
          label={t('check.number')}
          disabled
          checked={!customErrors.number}
        />
        <CustomInput
          type="checkbox"
          id="check-special-character"
          label={t('check.special-character')}
          disabled
          checked={!customErrors.specialCharacter}
        />
        <CustomInput
          type="checkbox"
          id="check-min-amount"
          label={t('check.min-amount')}
          disabled
          checked={!customErrors.minAmount}
        />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="confirmationCode">{t('code-confirmation')}</Label>
        <Input
          type="text"
          id="confirmationCode"
          name="confirmationCode"
          onChange={handleChange}
          maxLength={6}
          required
          value={values.confirmationCode}
          invalid={touched.confirmationCode && !!errors.confirmationCode}
        />
        <FormFeedback>{t('errors.code')}</FormFeedback>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="password">{t('label')}</Label>
        <InputWrapper>
          <Field
            autoComplete="new-password"
            name="password"
            type={visibility.password ? 'text' : 'password'}
            validate={validatePassword}
            className={classnames(
              'form-control',
              touched.password &&
                (Object.values(customErrors).some(x => x) ||
                  !!errors.password) &&
                'is-invalid',
            )}
          />
          {renderVisibilityToggle('password')}
        </InputWrapper>
        {touched.password &&
          (Object.values(customErrors).some(x => x) || !!errors.password) && (
            <ErrorMessage>
              {customErrors.required
                ? t('errors.password')
                : t('errors.others')}
            </ErrorMessage>
          )}
      </FormGroup>
      <FormGroup>
        <Label htmlFor="passwordConfirmation">{t('label-confirm')}</Label>
        <InputWrapper>
          <Input
            type={visibility.passwordConfirmation ? 'text' : 'password'}
            autoComplete="new-password"
            id="passwordConfirmation"
            name="passwordConfirmation"
            onChange={handleChange}
            required
            value={values.passwordConfirmation}
            invalid={
              touched.passwordConfirmation && !!errors.passwordConfirmation
            }
          />
          {renderVisibilityToggle('passwordConfirmation')}
        </InputWrapper>
        {touched.passwordConfirmation && !!errors.passwordConfirmation && (
          <ErrorMessage>
            {errors.passwordConfirmation &&
            errors.passwordConfirmation.includes('required')
              ? t('errors.password')
              : t('errors.password-confirmation')}
          </ErrorMessage>
        )}
      </FormGroup>
      <br />
      <ButtonSubmit
        block
        color="primary"
        type="submit"
        loading={submitting ? 1 : 0}
      >
        {t(`${submitText}${submitting ? 'ting' : ''}`)}
        {submitting && <FaSpinner color="#FFF" size={14} />}
      </ButtonSubmit>
      {!hideLink && (
        <ButtonLink block color="link" onClick={goToLogin}>
          {t('back-to-login')}
        </ButtonLink>
      )}
    </Form>
  );
};

export default ResetPasswordForm;

ResetPasswordForm.propTypes = {
  hideLink: propTypes.bool,
  submitText: propTypes.string,
};

ResetPasswordForm.defaultProps = {
  hideLink: false,
  submitText: 'submit',
};
